import type { CSSProperties } from "react";
import type { BrandColors, TemplateLayerStyle } from "../template-types";

/**
 * Resolves a color dynamically by mapping template defaults to current brand colors.
 * This is THE SINGLE SOURCE OF TRUTH for color resolution across all components.
 * 
 * @param templateColor - The color defined in the template
 * @param templateDefaults - The default brand colors from the template
 * @param currentBrandColors - The current brand colors selected by the user
 * @param fallback - Fallback color if no match is found
 * @returns The resolved color
 */
/**
 * Normalizes similar colors for comparison.
 * Groups similar dark colors together so they all map to primaryText.
 */
function normalizeColorForMatching(color: string): string {
  const normalized = color.toLowerCase().trim();
  
  // Group common dark/black colors together
  const darkColors = ['#000000', '#000', '#111827', '#1f2937', '#374151', '#1a1a1a'];
  if (darkColors.some(dark => normalized === dark)) {
    return '#000000';
  }
  
  return normalized;
}

export function resolveDynamicColor(
  templateColor: string | null | undefined,
  templateDefaults: BrandColors,
  currentBrandColors: BrandColors,
  fallback: string,
): string {
  if (!templateColor) return fallback;
  
  // Normalize colors for comparison (lowercase and trim)
  const normalizedTemplateColor = normalizeColorForMatching(templateColor);
  const normalizedBackground = normalizeColorForMatching(templateDefaults.background);
  const normalizedPrimaryText = normalizeColorForMatching(templateDefaults.primaryText);
  const normalizedAccent = normalizeColorForMatching(templateDefaults.accent);
  const normalizedRatingStar = normalizeColorForMatching(templateDefaults.ratingStar);
  
  // Check if the template color matches any of the template's default brand colors
  // If so, replace it with the corresponding current brand color
  if (normalizedTemplateColor === normalizedBackground) {
    return currentBrandColors.background;
  }
  if (normalizedTemplateColor === normalizedPrimaryText) {
    return currentBrandColors.primaryText;
  }
  if (normalizedTemplateColor === normalizedAccent) {
    return currentBrandColors.accent;
  }
  if (normalizedTemplateColor === normalizedRatingStar) {
    return currentBrandColors.ratingStar;
  }
  
  // If it doesn't match a default, use it as-is (custom color)
  return templateColor;
}

/**
 * Special resolver for rating star colors.
 * Templates that use accent for stars should now use the ratingStar color.
 * This ensures backward compatibility while using the new ratingStar color.
 */
export function resolveRatingStarColor(
  templateColor: string | null | undefined,
  templateDefaults: BrandColors,
  currentBrandColors: BrandColors,
): string {
  if (!templateColor) return currentBrandColors.ratingStar;
  
  const normalizedTemplateColor = templateColor.toLowerCase().trim();
  const normalizedRatingStar = templateDefaults.ratingStar.toLowerCase().trim();
  
  // If it explicitly uses ratingStar, map to current ratingStar
  if (normalizedTemplateColor === normalizedRatingStar) {
    return currentBrandColors.ratingStar;
  }
  
  // Use dynamic color resolution for other cases
  return resolveDynamicColor(templateColor, templateDefaults, currentBrandColors, currentBrandColors.ratingStar);
}

/**
 * Maps template layer style to CSS properties with dynamic color resolution.
 * This ensures consistent styling across all text elements.
 */
export function mapTextStyle(
  style: TemplateLayerStyle | null | undefined,
  brandColors: BrandColors,
  templateDefaults: BrandColors,
  overrides: CSSProperties = {},
): CSSProperties {
  if (!style) return overrides;
  
  // Resolve text color dynamically
  const resolvedColor = style.color
    ? resolveDynamicColor(style.color, templateDefaults, brandColors, brandColors.primaryText)
    : brandColors.primaryText;
  
  // Resolve background color dynamically
  const resolvedBackgroundColor = style.backgroundColor
    ? resolveDynamicColor(style.backgroundColor, templateDefaults, brandColors, style.backgroundColor)
    : undefined;
  
  // Resolve border color dynamically
  const resolvedBorderColor = style.borderColor
    ? resolveDynamicColor(style.borderColor, templateDefaults, brandColors, style.borderColor)
    : undefined;
  
  const baseStyle: CSSProperties = {
    fontFamily: style.fontFamily ?? undefined,
    fontSize: style.fontSize ? `${style.fontSize}px` : undefined,
    fontWeight: style.fontWeight ?? undefined,
    lineHeight: style.lineHeight !== null && style.lineHeight !== undefined
      ? style.lineHeight > 4
        ? `${style.lineHeight}px`
        : `${style.lineHeight}`
      : undefined,
    letterSpacing: style.letterSpacing ? `${style.letterSpacing}px` : undefined,
    textAlign: style.textAlign ?? undefined,
    textTransform: style.textTransform ?? undefined,
    textDecoration: style.textDecoration ?? undefined,
    color: resolvedColor,
    backgroundColor: resolvedBackgroundColor,
    borderColor: resolvedBorderColor,
    borderWidth: style.borderWidth ? `${style.borderWidth}px` : undefined,
    borderRadius: style.borderRadius ? `${style.borderRadius}px` : undefined,
    borderStyle: style.borderWidth ? "solid" : undefined,
    opacity: style.opacity ?? undefined,
    boxShadow: style.shadow ?? undefined,
    textShadow: style.textShadow ?? undefined,
    overflowWrap: style.overflowWrap ?? undefined,
    whiteSpace: style.whiteSpace ?? undefined,
    textOverflow: style.textOverflow ?? undefined,
  };
  
  // Handle padding (axis-based or uniform)
  if (style.paddingX !== undefined || style.paddingY !== undefined) {
    baseStyle.padding = `${style.paddingY ?? 0}px ${style.paddingX ?? 0}px`;
  } else if (typeof style.padding === "number") {
    baseStyle.padding = `${style.padding}px`;
  } else if (typeof style.padding === "string") {
    baseStyle.padding = style.padding;
  } else if (Array.isArray(style.padding) && style.padding.length > 0) {
    baseStyle.padding = style.padding.map(p => typeof p === "number" ? `${p}px` : p).join(" ");
  }
  
  // Apply overrides last
  return { ...baseStyle, ...overrides };
}

/**
 * Normalizes padding from various formats to a CSS string
 */
export function normalizePadding(style?: TemplateLayerStyle | null): string | undefined {
  if (!style) return undefined;
  
  if (typeof style.padding === "number") {
    return `${style.padding}px`;
  }
  
  if (typeof style.padding === "string") {
    return style.padding;
  }
  
  if (Array.isArray(style.padding) && style.padding.length > 0) {
    return style.padding.map(p => typeof p === "number" ? `${p}px` : p).join(" ");
  }
  
  if (typeof style.paddingX === "number" || typeof style.paddingY === "number") {
    return `${style.paddingY ?? 0}px ${style.paddingX ?? 0}px`;
  }
  
  return undefined;
}

/**
 * Calculates effective font size with auto-reduction if enabled.
 * Prevents long text from overflowing by reducing font size.
 */
export function calculateAutoFontSize(
  textContent: string,
  baseFontSize: number,
  minFontSize: number | null | undefined,
  autoReduce: boolean,
): number {
  if (!autoReduce || !textContent) {
    return baseFontSize;
  }
  
  const resolvedMinSize = minFontSize ?? Math.max(8, Math.floor(baseFontSize * 0.5));
  const textLength = textContent.length;
  const baseLength = 150; // Characters that fit comfortably
  
  if (textLength <= baseLength) {
    return baseFontSize;
  }
  
  // Calculate reduction factor based on excess text
  const excessRatio = (textLength - baseLength) / baseLength;
  const reductionFactor = Math.min(1, 1 / (1 + excessRatio * 0.5));
  
  return Math.max(resolvedMinSize, Math.floor(baseFontSize * reductionFactor));
}

/**
 * Gets line clamp styles for limiting text to a specific number of lines
 */
export function getLineClampStyles(maxLines: number | null | undefined): CSSProperties {
  if (!maxLines || maxLines <= 0) return {};
  
  return {
    display: "-webkit-box",
    WebkitLineClamp: maxLines,
    WebkitBoxOrient: "vertical" as const,
    overflow: "hidden",
  };
}

/**
 * Gets vertical alignment styles
 */
export function getVerticalAlignStyles(
  verticalAlign: "top" | "middle" | "bottom" | null | undefined
): CSSProperties {
  if (!verticalAlign || verticalAlign === "top") return {};
  
  if (verticalAlign === "middle") {
    return {
      display: "flex",
      alignItems: "center",
    };
  }
  
  if (verticalAlign === "bottom") {
    return {
      display: "flex",
      alignItems: "flex-end",
    };
  }
  
  return {};
}
